import { checkPermissions } from "@elit-hair/elit-tools";
import {
    Card,
    CardContent,
    DatePickerMode,
    DateRangePicker,
    EmptyState,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    useIsMobile,
} from "@elit-hair/elit-ui";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { PageActions } from "@/components/page-actions";
import { PageFiltersDialog } from "@/components/page-filters-dialog";
import { PERMISSIONS } from "@/constants/permissions";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/useDashboardRole";
import { usePriorityRecordCounts } from "@/queries/lead.queries";
import type { PageFilterSummaryItem } from "@/types/page-filters.types";
import { convertFromDateRange } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { REPORT_MAIN_CATEGORY_IDS } from "../reports/types";
import { PriorityReportDeptSelector } from "./components/priority-report-dept-selector";
import { usePriorityReportDepartments } from "./hooks/use-priority-report-departments";
import {
    REPORT_COLORS,
    buildPriorityReportCountFilters,
    buildPriorityTableData,
    buildTotalsByDepartment,
    getCellColor,
    getPriorityReportMaxLevel,
} from "./utils/priority-report.utils";

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_SETTING_PRIORITY_REPORT)({
    beforeLoad: () => {
        throw redirect({
            to: APP_ROUTES_TO.PERFORMANCE_REPORTS,
            search: { category: REPORT_MAIN_CATEGORY_IDS.SETTING_CAPACITIES },
        });
    },
    component: SettingsPriorityReport,
    staticData: {
        permissions: [PERMISSIONS.CALL_SETTING_PRIORITY_COUNTS],
    },
});

export function SettingsPriorityReport() {
    const { t } = useTranslation();
    const { permissionCodes } = useDashboardRole();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
    const [dateMode, setDateMode] = useState<DatePickerMode>(DatePickerMode.RANGE);

    const { activeDepartments } = usePriorityReportDepartments();

    const maxPriority = getPriorityReportMaxLevel();

    const canOpenLeads = useMemo(
        () => checkPermissions(permissionCodes, [PERMISSIONS.CALL_SETTING_PRIORITY_READ_LEADS]),
        [permissionCodes]
    );

    const { startDate, endDate } = useMemo(() => convertFromDateRange(dateRange), [dateRange]);

    const countFilters = useMemo(() => buildPriorityReportCountFilters(activeDepartments), [activeDepartments]);

    const { data: priorityRecordCounts = [], isLoading } = usePriorityRecordCounts({
        start: startDate,
        end: endDate,
        filters: countFilters,
    });

    const tableData = useMemo(
        () => buildPriorityTableData(priorityRecordCounts, activeDepartments, maxPriority),
        [priorityRecordCounts, activeDepartments, maxPriority]
    );

    const totalsByDepartment = useMemo(() => buildTotalsByDepartment(tableData), [tableData]);

    const filterSummary = useMemo<PageFilterSummaryItem[]>(
        () => activeDepartments.map((dept) => ({ id: dept.key, label: dept.label })),
        [activeDepartments]
    );

    const handleOpenSettingPriorityLeadsPage = (deptKey: string, priority: number) => {
        if (!canOpenLeads) return;

        navigate({
            to: APP_ROUTES_TO.PERFORMANCE_SETTING_PRIORITY_LEADS,
            params: { language: deptKey, priority: priority.toString() },
        });
    };

    return (
        <div className="bg-background p-4 md:p-6">
            <PageFiltersDialog summary={filterSummary}>
                <PriorityReportDeptSelector />
            </PageFiltersDialog>
            <PageActions>
                <DateRangePicker
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    showModeSwitcher
                    mode={dateMode}
                    onModeChange={setDateMode}
                    maxDate={DateTime.now().toJSDate()}
                    numberOfMonths={isMobile ? 1 : 2}
                    showTodayButton
                    showClearButton
                    placeholder={t("select_date_range")}
                    buttonWidth="w-56"
                />
            </PageActions>
            <div className="mx-auto max-w-6xl space-y-4">
                {/* Compact Table Card */}
                <Card className="border-border/40 shadow-sm relative min-h-[200px]">
                    {isLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px]">
                            <Spinner size="lg" />
                        </div>
                    )}
                    <CardContent className="p-4">
                        {activeDepartments.length === 0 && !isLoading ? (
                            <EmptyState variant="simple" title="priority_report_select_collection" />
                        ) : (
                            <div className="overflow-auto rounded-lg border border-border/40">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-12 font-semibold text-sm text-center">
                                                {t("priority")}
                                            </TableHead>
                                            {activeDepartments.map((dept) => (
                                                <TableHead
                                                    key={dept.key}
                                                    className="text-center font-semibold text-sm min-w-24"
                                                >
                                                    {dept.label}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {tableData.map((row) => (
                                            <TableRow
                                                key={row.priority}
                                                className="hover:bg-muted/30 transition-colors"
                                            >
                                                <TableCell className="py-2">
                                                    <div className="flex justify-center items-center">
                                                        <div
                                                            className={
                                                                row.isExtra
                                                                    ? "flex h-16 min-w-16 items-center justify-center rounded bg-primary/10 px-2 text-sm font-bold text-primary"
                                                                    : "flex h-16 w-16 items-center justify-center rounded bg-primary/10 text-lg font-bold text-primary"
                                                            }
                                                        >
                                                            {row.isExtra ? t("priority_extra") : row.priority}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                {activeDepartments.map((dept) => {
                                                    const count = row.counts[dept.key];
                                                    return (
                                                        <TableCell key={dept.key} className="p-1.5">
                                                            <div
                                                                className={
                                                                    canOpenLeads
                                                                        ? "flex h-16 w-full cursor-pointer items-center justify-center rounded-md text-lg font-bold text-white shadow-sm transition-[filter] hover:brightness-110"
                                                                        : "pointer-events-none flex h-16 w-full cursor-default items-center justify-center rounded-md text-lg font-bold text-white shadow-sm"
                                                                }
                                                                style={{
                                                                    backgroundColor: getCellColor(row.priority, count),
                                                                }}
                                                                onClick={
                                                                    canOpenLeads
                                                                        ? () => {
                                                                              handleOpenSettingPriorityLeadsPage(
                                                                                  dept.key,
                                                                                  row.priority
                                                                              );
                                                                          }
                                                                        : undefined
                                                                }
                                                                role={canOpenLeads ? "button" : undefined}
                                                                tabIndex={canOpenLeads ? 0 : undefined}
                                                                aria-disabled={!canOpenLeads}
                                                            >
                                                                {count}
                                                            </div>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                        {/* Totals row */}
                                        <TableRow className="bg-muted/60 font-semibold hover:bg-muted/60">
                                            <TableCell className="py-2">
                                                <div className="flex justify-center items-center font-semibold">
                                                    {t("total")}
                                                </div>
                                            </TableCell>
                                            {activeDepartments.map((dept) => {
                                                const count = totalsByDepartment[dept.key] ?? 0;
                                                return (
                                                    <TableCell key={dept.key} className="p-1.5">
                                                        <div className="flex h-12 w-full items-center justify-center rounded-md text-lg font-bold text-primary shadow-sm">
                                                            {count}
                                                        </div>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Legend */}
                <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: REPORT_COLORS.green }} />
                        <span>{t("priority_report_green")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: REPORT_COLORS.yellow }} />
                        <span>{t("priority_report_yellow")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: REPORT_COLORS.red }} />
                        <span>{t("priority_report_red")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
